import { MouseEvent } from "react";

import trackEvent from "@analytics/utils/trackEvent";

import { DataSourceType } from "@interfaces/company.type";

import { ICompanyTableControls } from "@store/filters/filter.types";

import { getKeysOfValuesChanged } from "@utilities/diffDictionaries";

const identifyUser = (firstName: string, lastName: string, email: string, userId: string) => {
  // @ts-expect-error - unsafe code
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call
  globalThis.analytics.identify(userId, {
    email,
    firstName,
    lastName,
  });
};

const UserAnalyticsService = {
  handleLinkAnalytics: (event: MouseEvent<HTMLElement | SVGSVGElement>, link: string, type: string) => {
    event.preventDefault();
    window.open(link, "_blank", "noopener,noreferrer");
    trackEvent("External Link Clicked", { destination_url: link, type });
  },
  trackBreadcrumbsNavigation: (workflow: string, from: string, to: string) =>
    trackEvent("Breadcrumbs Navigation Clicked", {
      from,
      to,
      workflow,
    }),
  trackCompaniesDownloaded: (selectedCompaniesLength: number, workflow: string) =>
    trackEvent("File Downloaded", {
      content: "companies",
      file_extension: "csv",
      file_name: "companyList.csv",
      row_count: selectedCompaniesLength,
      type: "company",
      workflow,
    }),
  trackCompanyFilter: (
    newCompanyFilters: ICompanyTableControls,
    oldCompanyFilters: ICompanyTableControls,
    workflow: string,
  ) => {
    const modifiedFilters = getKeysOfValuesChanged(oldCompanyFilters.filters, newCompanyFilters.filters).filter(
      (filter) => filter !== "search",
    );
    if (modifiedFilters.length > 0) {
      trackEvent("Filter Applied", {
        modified_filters: modifiedFilters,
        type: "Company",
        workflow,
      });
    }
  },
  trackCompanyProfileTab: (tabType: string, tabIndex: number) =>
    trackEvent("Company Profile Tab Clicked", {
      tab: tabType,
      tab_index: tabIndex,
    }),
  trackCompanyRowClicked: (
    name: string,
    innovationMatch: number,
    numberOfPatents: string,
    numberOfEmployees: number,
    workflow: string,
  ) =>
    trackEvent("Company Row Clicked", {
      innovation_match: innovationMatch,
      name,
      number_of_employees: numberOfEmployees,
      number_of_patents: numberOfPatents,
      workflow,
    }),
  trackCompanySearchBarUsed: (query: string) => {
    // We only want to track the search bar used event if the query is not empty
    if (query.trim().length > 0) {
      trackEvent("Company Search Bar Used", { query });
    }
  },
  trackDataSourceLoaded: (
    dataSource: {
      id: string;
      source: DataSourceType | undefined;
    },
    workflow: string,
  ) =>
    trackEvent("Data Source Loaded", {
      data_source: dataSource,
      workflow,
    }),
  trackDirectorySearchResults: (query: ICompanyTableControls, companiesLength: number) =>
    trackEvent("Index Search Performed", {
      query,
      total_result_count: companiesLength,
    }),
  trackFailedLogin: (email: string, reason: string) =>
    trackEvent("User Sign In Failed", {
      email,
      reason,
    }),
  trackFailedSignup: (firstName: string, lastName: string, email: string, reason: string) =>
    trackEvent("User Sign Up Failed", {
      email,
      first_name: firstName,
      last_name: lastName,
      reason,
    }),
  trackLogin: () => {
    trackEvent("User Signed In", {});
  },
  trackOutboundClick: (destinationUrl: string, type: string) =>
    trackEvent("External Link Clicked", {
      destination_url: destinationUrl,
      type,
    }),
  trackRefresh: () => {
    trackEvent("User Refreshed", {});
  },
  trackResourceLink: (resourceLink: string, workflow: string) =>
    trackEvent("Resource Link Clicked", {
      resourceLink,
      workflow,
    }),
  trackSignup: (firstName: string, lastName: string, email: string, userId: string) => {
    identifyUser(firstName, lastName, email, userId);
    trackEvent("User Signed Up", {});
  },
  userAcknowledgedProductUpdate: (id: string, firstName: string, lastName: string) =>
    trackEvent("Product Update Acknowledged", {
      firstName,
      id,
      lastName,
    }),
} as const;

export default UserAnalyticsService;
